import { createFileRoute } from "@tanstack/react-router";

import { ORG_POSTAL } from "@/lib/email-preferences";

const CANDID_URL = "https://app.candid.org/profile/14586966/t1d-warriors-inc-88-1617933";
const description =
  "How T1D Warriors collects, uses, and protects the information of volunteers, event helpers, and the families we serve.";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy policy | T1D Warriors" },
      { name: "description", content: description },
      { property: "og:title", content: "Privacy policy | T1D Warriors" },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://volunteer.t1dw.org/privacy" }],
  }),
  component: PrivacyPage,
});

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="text-base font-semibold text-[#0a1f2e]">{title}</h2>
      <div className="mt-2 space-y-3 text-sm leading-relaxed text-[#2c4a5c]">{children}</div>
    </section>
  );
}

function PrivacyPage() {
  return (
    <main className="app-light min-h-screen bg-[#F1F9FC] px-5 py-14">
      <article className="mx-auto w-full max-w-2xl rounded-md border border-[#d6e7f0] bg-white p-8">
        <h1 className="text-2xl font-semibold text-[#0a1f2e]">Privacy policy</h1>
        <p className="mt-2 text-sm text-[#5a7386]">Last updated August 27, 2026.</p>

        <p className="mt-5 text-sm leading-relaxed text-[#2c4a5c]">
          T1D Warriors is a small, family-run 501(c)(3) nonprofit in Burlington, Kentucky, EIN
          88-1617933. You can verify us on{" "}
          <a className="text-[#199CD6] underline" href={CANDID_URL}>
            Candid (GuideStar)
          </a>
          . This page explains what we collect through our volunteer app and our email, and what we
          do with it.
        </p>

        <Section title="What we collect">
          <p>
            Information you give us: your name, email address, phone number, city and state, the
            programs you are interested in, your availability, and anything you type into a
            volunteer application, event sign up form, or message to us.
          </p>
          <p>
            Information we record automatically: whether an email we sent was delivered, opened, or
            clicked, and basic app usage such as sign in times and time logged against volunteer
            work.
          </p>
        </Section>

        <Section title="How we use it">
          <p>
            To run volunteer operations: reviewing applications, scheduling event shifts, sending
            confirmations and reminders, coordinating outreach, and tracking volunteer hours.
          </p>
          <p>
            To send you email you asked for. Every non-essential email carries a link to choose
            which categories you receive and a link to unsubscribe from all of them. Confirmations
            and account email always keep working.
          </p>
        </Section>

        <Section title="What we do not do">
          <p>
            We never sell, rent, or trade your information. We do not share it with advertisers. We
            do not send text messages unless you gave express written consent, and you can reply
            STOP at any time.
          </p>
        </Section>

        <Section title="Who else sees it">
          <p>
            Only the service providers we need to operate: our hosting and database provider, our
            email delivery provider, and error monitoring. They process data on our behalf and are
            not permitted to use it for anything else. We may disclose information if the law
            requires it.
          </p>
        </Section>

        <Section title="How long we keep it">
          <p>
            We keep volunteer records for as long as you are involved with us, and afterwards only
            as long as needed for our records and legal obligations. Ask us to delete your
            information and we will, unless we are required to keep it.
          </p>
        </Section>

        <Section title="Your choices">
          <p>
            You can ask to see, correct, or delete the information we hold about you, and you can
            change your email preferences at any time using the link at the bottom of any email
            from us. Email{" "}
            <a className="text-[#199CD6] underline" href={`mailto:${ORG_POSTAL.contactEmail}`}>
              {ORG_POSTAL.contactEmail}
            </a>{" "}
            and a real person will handle it.
          </p>
        </Section>

        <Section title="Children">
          <p>
            Our programs serve families with children, but this app is intended for adults. We do
            not knowingly collect information directly from children under 13 without a parent or
            guardian.
          </p>
        </Section>

        <p className="mt-10 border-t border-[#e6eff5] pt-4 text-xs leading-relaxed text-[#7b8f9c]">
          {ORG_POSTAL.name}, a registered 501(c)(3) nonprofit. EIN 88-1617933. {ORG_POSTAL.line1},{" "}
          {ORG_POSTAL.line2}. Questions?{" "}
          <a className="underline" href={`mailto:${ORG_POSTAL.contactEmail}`}>
            {ORG_POSTAL.contactEmail}
          </a>
        </p>
      </article>
    </main>
  );
}
